import React, {
  useContext,
  DragEvent,
  ChangeEvent,
  useState,
  useEffect,
} from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FlowScreenWrapper } from "../shared/FlowScreenWrapper";
import { AuthContext } from "../../../AuthContextWrapper";
import {
  FILE_UPLOAD_TYPES,
  MAX_UPLOAD_BYTES,
  OUTCOMES,
} from "../../../constants";
import { Box, Button, Typography } from "@mui/material";
import simplePurpleHouse1 from "../../assets/simple-purple-house-1x.png";
import simplePurpleHouse15 from "../../assets/simple-purple-house-1.5x.png";
import simplePurpleHouse2 from "../../assets/simple-purple-house-2x.png";
import simplePurpleHouse3 from "../../assets/simple-purple-house-3x.png";
import { ScreenFlowFormButtons } from "../shared/ScreenFlowFormButtons";
import { AssetContext } from "../../../AssetContextWrapper";
import { CancelToken, estateApi, laravelApiV1, network } from "../../../apis";
import { AssetImage } from "../../../models/AssetImage";
import { Loading } from "../../shared/Loading";

const NUM_RETRIES = 10;
const RETRY_DELAY = 1000;
const HOME_IMAGE_DIMENSIONS = "150x150";

export const HomePhotoScreen = (): JSX.Element => {
  const navigate = useNavigate();
  const { flow, activeEstate } = useContext(AuthContext);
  const { assetsLoaded } = useContext(AssetContext);
  const { screenName } = useParams();
  const cancelTokenSource = CancelToken.source();

  const currentScreen = flow?.getCurrentFlowScreen(screenName);
  const primaryRealEstate = activeEstate?.primaryRealEstate;

  const inputRef = React.useRef(null);

  const [errors, setErrors] = useState<string[]>();
  const [dragActive, setDragActive] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [assetImagePath, setAssetImagePath] = useState<string>();
  const [processingImage, setProcessingImage] = useState(false);

  useEffect(() => {
    (async () => {
      if (assetsLoaded && primaryRealEstate) {
        if (primaryRealEstate?.images?.length) {
          // get this assets newest image
          const [firstImage] = primaryRealEstate?.images.sort(
            (a: AssetImage, b: AssetImage) =>
              (b?.updatedAt?.getTime() || 0) - (a?.updatedAt?.getTime() || 0)
          );
          // console.log(firstImage?.fileName);
          if (firstImage && firstImage?.fileName) {
            setHomeImage(firstImage.fileName, 1, true);
          }
        }
      }
    })();
  }, [assetsLoaded]);

  const setHomeImage = async (
    fileName: string,
    tryNumber = 1,
    screenInit = false
  ): Promise<void> => {
    if (tryNumber < NUM_RETRIES) {
      // https://app.weareatticus.com/api/v1/asset/image/pwL65YV4VFwOWrYVmcHg9j1IuAoFOUUnv0Nt5nZJqaVWuelD/238x238
      const homeImagePath = `${laravelApiV1}${AssetImage.path}/${fileName}/${HOME_IMAGE_DIMENSIONS}`;
      try {
        const response = await network.callGet(homeImagePath, true, {
          responseType: "blob",
          cancelToken: cancelTokenSource.token,
        });
        if (response?.data) {
          const file = new Blob([response.data], {
            type: response?.headers["content-type"] || "image/jpg",
          });
          const urlCreator = window.URL || window.webkitURL;
          const imageUrl = urlCreator.createObjectURL(file);
          setAssetImagePath(imageUrl);
          setProcessingImage(false);
        } else {
          // fail silently
        }
      } catch (error) {
        // possibly not created yet, keep trying, unless this is the initial screen load
        if ((error as any)?.response?.status === 404 && !screenInit) {
          setProcessingImage(true);
          setTimeout(() => {
            (async () => {
              await setHomeImage(fileName, tryNumber + 1);
            })();
          }, RETRY_DELAY);
        }
      }
    } else {
      setProcessingImage(false);
      setAssetImagePath(undefined);
    }
  };

  // handle drag events
  const handleDrag = function (e: DragEvent<HTMLFormElement | HTMLDivElement>) {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  // triggers when file is dropped
  const handleDrop = function (e: DragEvent<HTMLDivElement>) {
    setErrors(undefined);
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      // at least one file has been dropped so do something
      validateAndUpload(e.dataTransfer.files);
    }
  };

  // triggers when file is selected with click
  const handleChange = function (e: ChangeEvent<HTMLInputElement>) {
    setErrors(undefined);
    e.preventDefault();
    if (e.target.files && e.target.files[0]) {
      // at least one file has been selected so do something
      // console.log(e.target.files);
      validateAndUpload(e.target.files);
    }
  };

  const validateAndUpload = async (files: FileList): Promise<void> => {
    if (files.length === 0) {
      setErrors(["Please drop or select a file."]);
      return;
    }
    // allow multiple?
    const errs: string[] = [];
    for (let i = 0; i < files.length; i++) {
      const file = files.item(i);
      if (file) {
        if (file.size > MAX_UPLOAD_BYTES) {
          console.error(file.size, ">", MAX_UPLOAD_BYTES);
          errs.push(`"${file.name}" is too large`);
        }
        if (!FILE_UPLOAD_TYPES.includes(file.type)) {
          console.error(file.type, "!includes", FILE_UPLOAD_TYPES);
          errs.push(`"${file.name}" file type: ${file.type} not accepted`);
        }
      } else {
        errs.push("no file");
      }
    }
    if (errs?.length) {
      setErrors(errs);
    } else {
      if (!(await handleUpload(files))) {
        setErrors(["Sorry, upload failed.  Please try again."]);
      }
    }
  };

  // triggers the input when the button is clicked
  const handleChooseClick = () => {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    inputRef?.current?.click();
  };

  const handleUpload = async (files: FileList): Promise<boolean> => {
    setIsSubmitting(true);
    const file = files.item(0);
    if (file) {
      // console.log("uploading file", file);
      const uploadResult = await estateApi.saveAssetImage(
        file,
        primaryRealEstate?.id
      );
      if (uploadResult?.file_name) {
        setIsSubmitting(false);
        setHomeImage(uploadResult.file_name);
        return true;
      }
    }
    setIsSubmitting(false);
    setErrors(["Sorry, file upload failed.  Please try again."]);
    return false;
  };

  const handleComplete = async (): Promise<boolean> => {
    if (!currentScreen) throw Error("couldn't determine currentScreen");

    flow?.navigateToNext(currentScreen, OUTCOMES.complete, navigate);
    return true;
  };

  const getHomeElement = (): JSX.Element => {
    if (processingImage || isSubmitting) {
      return <Loading />;
    } else if (assetImagePath) {
      return (
        <img
          src={assetImagePath}
          alt="Primary Home"
          title="Primary Home"
          width="150"
          height="150"
          style={{
            borderRadius: 8,
          }}
        />
      );
    } else {
      return (
        <img
          srcSet={`${simplePurpleHouse15} 1.5x, ${simplePurpleHouse2} 2x, ${simplePurpleHouse3} 3x`}
          src={simplePurpleHouse1}
          alt="Primary Home"
          title="Primary Home"
          width="150"
          height="150"
        />
      );
    }
  };

  return currentScreen ? (
    <FlowScreenWrapper title="Please upload a picture of the home. This will make it easy for you to find it quickly in your Atticus Digital Inventory of Assets.">
      <>
        <Box
          sx={{
            display: "flex",
            flexDirection: {
              sm: "row",
              xs: "column",
            },
            alignItems: "flex-start",
            justifyContent: "space-between",
          }}
        >
          <Box
            sx={{
              width: 150,
              height: 150,
              borderRadius: 2,
              boxShadow: 2,
              mb: {
                sm: "auto",
                xs: 2,
              },
              mr: 2,
            }}
          >
            {getHomeElement()}
          </Box>
          <Box
            sx={{
              flexGrow: 1,
              borderWidth: 2,
              borderRadius: 2,
              borderStyle: "dashed",
              borderColor: "secondary.light",
              backgroundColor: dragActive ? "white" : "success.light",
            }}
          >
            <form
              id="form-file-upload"
              style={{
                textAlign: "center",
                position: "relative",
              }}
              onDragEnter={handleDrag}
              onSubmit={(e) => e.preventDefault()}
            >
              <input
                type="file"
                ref={inputRef}
                id="input-file-upload"
                multiple={true}
                style={{ display: "none" }}
                onChange={handleChange}
              />
              <label
                id="label-file-upload"
                htmlFor="input-file-upload"
                style={{
                  display: "flex",
                  cursor: "pointer",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: "48px 16px",
                }}
              >
                {dragActive && (
                  <Box
                    id="drag-file-element"
                    onDragEnter={handleDrag}
                    onDragLeave={handleDrag}
                    onDragOver={handleDrag}
                    onDrop={handleDrop}
                    sx={{
                      position: "absolute",
                      width: "100%",
                      height: "100%",
                      borderRadius: 0,
                      top: 0,
                      right: 0,
                      bottom: 0,
                      left: 0,
                    }}
                  />
                )}
                <div>
                  <Typography
                    variant="h4"
                    color="secondary"
                    sx={{
                      pb: 2,
                      fontWeight: 600,
                    }}
                  >
                    {assetImagePath
                      ? "Drag or upload a different file here"
                      : "Drag or upload a file here"}
                  </Typography>
                  <Typography
                    sx={{
                      pb: 2,
                    }}
                  >
                    Accepted file formats (5mb size limit): .jpg, .png, .gif
                  </Typography>
                  <Button
                    variant="contained"
                    color="secondary"
                    onClick={handleChooseClick}
                  >
                    Choose a file
                  </Button>
                </div>
              </label>
              {errors?.length &&
                errors.map((error: string) => (
                  <Typography color="error" key={error} sx={{ m: 1 }}>
                    {error}
                  </Typography>
                ))}
            </form>
          </Box>
        </Box>
        <form onSubmit={handleComplete}>
          <ScreenFlowFormButtons
            isSubmitting={isSubmitting}
            onBack={() => navigate(-1)}
          />
        </form>
      </>
    </FlowScreenWrapper>
  ) : (
    <></>
  );
};
